import React, { useState, useEffect } from 'react';
import { Box, Typography, Link, Container, Grid, Paper, Fade, Slide } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';

const Header = () => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [exitComplete, setExitComplete] = useState(true);

    // useEffect to disable scrolling when dropdown is open
    useEffect(() => {
        if (dropdownOpen) {
            // Disable scrolling
            document.body.style.overflow = 'hidden';
        } 

        // Cleanup function
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [dropdownOpen]); // Depenendency Array to trigger the effect

    const handleLogoClick = () => {
        if (!dropdownOpen) {
            setExitComplete(false);
        }
        setDropdownOpen(!dropdownOpen);
    };

    const handleExited = () => {
        setExitComplete(true);
    };

    // Document links
    const documents = [
        { title: 'Declaration of Independence', path: 'https://www.archives.gov/founding-docs/declaration-transcript', description: 'The formal statement of independence ratified by the Continental Congress on July 4, 1776.'},
        { title: 'United States Constitution', path: 'https://constitutioncenter.org/the-constitution/amendments', description: 'The supreme law of the United States, adopted on September 17, 1787.' },
        { title: 'Bill of Rights', path: 'https://www.archives.gov/founding-docs/bill-of-rights/what-does-it-say', description: 'The first ten amendments to the Constitution, ratified in 1791.' },
        { title: 'Federalist Papers', path: 'https://guides.loc.gov/federalist-papers/full-text', description: 'A collection of 85 articles and essays written to promote the ratification of the Constitution.' },
    ];

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                backgroundColor: 'white',
                borderBottom: '1px solid lightgrey',
                padding: '3px 0',
                position: 'relative',
                zIndex: 1100,
            }}>
                <Box sx={{ position: 'relative' }}>
                    <Box
                        component="img"
                        src="/wethepeople.png"
                        alt="we the people"
                        onClick={handleLogoClick}
                        sx={{
                            width: '120px', 
                            height: 'auto', 
                            objectFit: 'contain',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            transform: 'scale(1.1)',
                            '&:hover': {
                                transform: 'scale(1.15)'
                            }
                        }}
                    />
                </Box>
            </Box>

            {/* Use a single animation component for better exit control */}
            <Slide 
                direction="down" 
                in={dropdownOpen} 
                timeout={{ enter: 400, exit: 500 }}
                mountOnEnter
                unmountOnExit
                onExited={handleExited}
            >
                <Box
                    sx={{
                        position: 'fixed',
                        top: '45px',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgb(255, 255, 255)',
                        //backgroundColor: 'rgba(232, 199, 158, 0.98)',
                        zIndex: 1000,
                        overflowY: 'auto',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        borderTop: '1px solid #e0e0e0',
                    }}
                >
                    <Container maxWidth="lg" sx={{ pt: 5, pb: 8 }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            mb: 4,
                            opacity: 1,
                            transition: 'opacity 400ms',
                            ...((!dropdownOpen && !exitComplete) && {
                                opacity: 0,
                                transition: 'opacity 300ms',
                            })
                        }}>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                                Prominent Documents of the United States
                            </Typography>
                            <Box 
                                onClick={() => setDropdownOpen(false)}
                                sx={{ 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    '&:hover': { color: 'primary.main' }
                                }}
                            >
                                <CloseIcon />
                                <Typography>Close</Typography>
                            </Box>
                        </Box>
                        
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                mb: 4,
                                opacity: 1,
                                transition: 'opacity 600ms',
                                ...((!dropdownOpen && !exitComplete) && {
                                    opacity: 0,
                                    transition: 'opacity 250ms',
                                })
                            }}
                        >
                            These documents form the foundation of American democracy and governance. 
                            They establish the principles of liberty, justice, and equality that guide our nation.
                        </Typography>

                        <Grid container spacing={8}>
                            {documents.map((doc, index) => (
                                <Grid item xs={12} md={6} key={index} >
                                    <Paper 
                                        elevation={1}
                                        sx={{ 
                                            p: 2,
                                            height: '100%',
                                            transition: 'transform 0.3s, box-shadow 0.3s, opacity 0.3s',
                                            opacity: 1,
                                            transform: 'translateY(0)',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 3
                                            },
                                            ...((!dropdownOpen && !exitComplete) && {
                                                opacity: 0,
                                                transform: 'translateY(20px)',
                                                transition: `opacity 200ms, transform 300ms`,
                                                transitionDelay: `${(documents.length - index - 1) * 60}ms`
                                            }),
                                            ...(dropdownOpen && {
                                                opacity: 0,
                                                transform: 'translateY(20px)',
                                                animation: `fadeIn 500ms forwards ${index * 120}ms`
                                            }),
                                            '@keyframes fadeIn': {
                                                '0%': {
                                                    opacity: 0,
                                                    transform: 'translateY(20px)'
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                    transform: 'translateY(0)'
                                                }
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <DescriptionIcon fontSize="large" sx={{ mr: 2, color: 'primary.main' }} />
                                            <Typography variant="h6" component="h2">
                                                {doc.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            {doc.description}
                                        </Typography>
                                        <Link 
                                            href={doc.path}
                                            download
                                            target="blank"
                                            sx={{
                                                display: 'inline-block',
                                                backgroundColor: '#f5f5f5',
                                                color: 'text.primary',
                                                textDecoration: 'none',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 1,
                                                '&:hover': {
                                                    backgroundColor: '#e0e0e0'
                                                }
                                            }}
                                        >
                                            View
                                        </Link>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>
            </Slide>
        </Box>
    );
};

export default Header;